import { Button, Checkbox, Combobox, Dropdown, ExternalLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { isProduction } from '../../../constants/environment';
import { useEnv } from '../../../context/env-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { isSparkJsVersionAtLeast } from '../../../lib/sparkjs-version';
import { SPARKJS_V2_VERSION } from '../constants';
import { useSparkJsVersions } from '../hooks/use-sparkjs-versions';
import { useUpdateSparkJsVersion } from '../hooks/use-update-sparkjs-version';
import { WidgetSection } from './widget-section';

const CARD =
  'features:storefront_widgets.pages.storefront_widgets_page.cards.sparkjs_version' satisfies I18nPrefix;
const UPGRADE_GUIDE_URL = 'https://docs.sparklayer.io/frontend-integration#PmPYk';
const WHATS_NEW_URL = 'https://docs.sparklayer.io/whats-new';

export function SparkJsVersionCard() {
  const env = useEnv();
  const currentVersion = env.sparkjs_version ?? null;

  const { options: versionOptions, isLoading, isError, refetch } = useSparkJsVersions();
  const { mutate: saveVersion, isPending: isSaving } = useUpdateSparkJsVersion();

  const [pending, setPending] = useState<string | null>(currentVersion);
  const [readGuide, setReadGuide] = useState<boolean>(false);

  const sparkJsGte2 = isSparkJsVersionAtLeast(currentVersion, SPARKJS_V2_VERSION);
  const selectDisabled = !sparkJsGte2 && !readGuide;
  const canSave = pending !== null && pending !== currentVersion && !isSaving;

  return (
    <WidgetSection
      title={t(`${CARD}.title`)}
      subtitle={t(`${CARD}.subtitle`)}
      badges={sparkJsGte2 ? undefined : [{ text: t(`${CARD}.upgrade_required`), variant: 'white' }]}
    >
      {!sparkJsGte2 && (
        <div className="flex flex-col gap-2">
          <Text size="sm" variant="secondary">
            {t(`${CARD}.upgrade_note`)}{' '}
            <ExternalLink link={UPGRADE_GUIDE_URL}>{t(`${CARD}.upgrade_guide`)}</ExternalLink>
          </Text>
          <Checkbox
            name="sparkjs-migration-read-guide"
            label={t(`${CARD}.read_guide`)}
            checked={readGuide}
            onChange={() => setReadGuide((value) => !value)}
          />
        </div>
      )}

      <SparkSuspense
        isLoading={isLoading}
        isError={isError}
        onReload={() => void refetch()}
        errorMessage={t('errors:generic')}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            {isProduction ? (
              <Dropdown
                label={t(`${CARD}.version_label`)}
                options={versionOptions}
                value={pending ?? ''}
                disabled={selectDisabled || isSaving}
                onChange={setPending}
              />
            ) : (
              <Combobox
                label={t(`${CARD}.version_label`)}
                options={versionOptions}
                value={pending}
                disabled={selectDisabled || isSaving}
                onChange={setPending}
              />
            )}
          </div>
          <Button
            disabled={!canSave}
            loading={isSaving}
            onClick={() => pending && saveVersion(pending)}
          >
            {t(`${CARD}.save`)}
          </Button>
        </div>
      </SparkSuspense>

      <Text size="sm" variant="secondary">
        {t(`${CARD}.help`)}{' '}
        <ExternalLink link={WHATS_NEW_URL}>{t(`${CARD}.whats_new`)}</ExternalLink>
      </Text>
    </WidgetSection>
  );
}
